import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { accordionStyles, getAccordionSummaryStyles, accordianDetailsStyles } from './styles';
import {
  FileTypes,
  FileUploadStatus,
  LogCategory,
  ShiftType,
  useUpsertLogEntryMutation,
  useLogEntriesByReferenceQuery,
  useKpiQaqcLogEntriesQuery,
  useRemoveLogEntryAttachmentsMutation,
  useUploadLogEntryAttachmentsMutation,
  useUpdateLogEntryAttachmentFileStatusMutation,
  LogEntriesByReferenceDocument,
  LogTemplateType,
  useWorkpackForKpiTrackerQuery,
  GetAggregatedKpiLogEntriesDocument,
  GetLogEntryCardsDocument,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useGetPlatformId } from '@/hooks/useGetPlatformId';
import { useAtomValue } from 'jotai';
import * as kpiState from '@/modules/KpiTracker/state';
import { formatDateToYYYYMMDD } from '../../utils';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { UploadAttachmentsDialog, KpiAttachment } from './UploadAttachmentsDialog';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { urlToBase64, downloadFile } from '@/utils';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { useFetchCookies } from '@/hooks/useFetchCookies';
import { MediaViewer } from '@/components/shared/MediaViewer';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { FileDisplay } from '@/components/Analysis/shared/MediaUploadsAndViewer/FileDisplay';
import { KPI_LOG_ENTRY_ATTACHMENTS_COLLECTION_NAME } from '../../constants';
import { COLLECTION_NAMES } from '@/constants';

export type KpiTrackerAttachmentsSectionPlacement = 'loggingTab' | 'qaqcForm';

export type AttachmentsSectionProps = {
  placement?: KpiTrackerAttachmentsSectionPlacement;
  qaqcLogsTemplateId?: string;
  /** When true (e.g. QA/QC view mode), uploads and removes are disabled. */
  readOnly?: boolean;
};

export const AttachmentsSection = ({
  placement = 'loggingTab',
  qaqcLogsTemplateId,
  readOnly = false,
}: AttachmentsSectionProps = {}) => {
  const isQaqcPlacement = placement === 'qaqcForm';
  const urlStructureId = useGetStructureId();
  const kpiPanelStructureIdOverride = useAtomValue(kpiState.kpiPanelStructureIdOverride);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(true);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [attachments, setAttachments] = useState<KpiAttachment[]>([]);
  const [processedAttachments, setProcessedAttachments] = useState<Attachment[]>([]);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [attachmentIndexToRemove, setAttachmentIndexToRemove] = useState<number | null>(null);
  const [isProcessingAttachments, setIsProcessingAttachments] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const selectedWorkpackId = useAtomValue(kpiState.selectedWorkpackIdForKpiTracker);
  const selectedWorklogsTemplate = useAtomValue(kpiState.selectedWorklogsTemplate);
  const selectedCostLogsTemplate = useAtomValue(kpiState.selectedCostLogsTemplateForKpi);
  const selectedWbsFilter = useAtomValue(kpiState.selectedWbsFilter);
  const selectedDate = useAtomValue(kpiState.selectedDate);
  const selectedShift = useAtomValue(kpiState.selectedShift);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = useMemo(() => formatDateToYYYYMMDD(selectedDate), [selectedDate]);
  const { showSnackBar } = useSnackBarMessage({
    variant: 'filled',
    atom: kpiState.kpiPanelSnackbarMessage,
  });
  const cookiesFetched = useFetchCookies();
  const logType = useAtomValue(kpiState.selectedLogTemplateType);
  const platformId = useGetPlatformId();
  const isCostLog = !isQaqcPlacement && logType === LogTemplateType.CostLogTemplate;
  const referenceId = isQaqcPlacement
    ? (selectedWorkpackId ?? '')
    : isCostLog
      ? (platformId ?? '')
      : (selectedWorkpackId ?? '');
  const shift = isCostLog ? ShiftType.Day : (selectedShift ?? ShiftType.Day);
  const category = isQaqcPlacement
    ? LogCategory.QaqcForm
    : isCostLog
      ? LogCategory.CostLog
      : LogCategory.Worklog;

  const { data: workpackData } = useWorkpackForKpiTrackerQuery({
    variables: { workpackId: selectedWorkpackId },
    skip: !selectedWorkpackId || isCostLog,
  });
  const workpackStructureId = workpackData?.workpack?.structure?.id;

  const structureId =
    urlStructureId || (isCostLog ? kpiPanelStructureIdOverride : workpackStructureId) || '';

  const attachmentHelpText = useMemo(() => {
    if (isQaqcPlacement) {
      return {
        empty: readOnly
          ? 'No attachments for this shift.'
          : 'No attachments for this shift. Upload images or PDFs to attach to this QA/QC',
        withFiles: readOnly
          ? 'Files attached to this QA/QC for this shift.'
          : 'Upload images or PDFs to attach to this QA/QC',
      };
    }
    if (isCostLog) {
      return {
        empty: 'No attachments for this date. Upload images or PDFs to attach to this cost log.',
        withFiles: 'Upload images or PDFs to attach to this cost log.',
      };
    }
    return {
      empty: 'No attachments for this date. Upload images or PDFs to attach to this work log.',
      withFiles: 'Upload images or PDFs to attach to this work log.',
    };
  }, [isCostLog, isQaqcPlacement, readOnly]);

  const worklogQuerySkip =
    isQaqcPlacement || !structureId || (isCostLog ? !platformId : !selectedWorkpackId);

  const {
    data: logEntriesData,
    loading: worklogQueryLoading,
    refetch: refetchWorklogAttachments,
  } = useLogEntriesByReferenceQuery({
    variables: {
      input: {
        referenceId: referenceId,
        structureId: structureId ?? '',
        category: category,
        logDate: formattedDate,
        shift: shift,
        ...(selectedWbsFilter && { wbs: selectedWbsFilter }),
      },
    },
    skip: worklogQuerySkip,
    fetchPolicy: 'cache-first',
  });

  const qaqcLogEntriesInput = useMemo(
    () => ({
      collectionName: COLLECTION_NAMES.WORKPACK,
      structureId: structureId ?? undefined,
      referenceId: selectedWorkpackId ?? undefined,
      category: LogCategory.QaqcForm,
      logsTemplateId: qaqcLogsTemplateId,
      startDate: formattedDate,
      endDate: formattedDate,
      timezone: userTimezone,
      shift: selectedShift,
    }),
    [
      formattedDate,
      qaqcLogsTemplateId,
      selectedShift,
      selectedWorkpackId,
      structureId,
      userTimezone,
    ]
  );

  const shouldSkipQaqcQuery =
    !isQaqcPlacement || !structureId || !selectedWorkpackId || !qaqcLogsTemplateId;

  const {
    data: qaqcLogEntriesData,
    loading: qaqcQueryLoading,
    refetch: refetchQaqcAttachments,
  } = useKpiQaqcLogEntriesQuery({
    variables: { input: qaqcLogEntriesInput },
    skip: shouldSkipQaqcQuery,
    fetchPolicy: 'cache-and-network',
  });

  const currentLogEntry = useMemo(() => {
    if (isQaqcPlacement) {
      const list = qaqcLogEntriesData?.logEntries ?? [];
      if (!qaqcLogsTemplateId) {
        return null;
      }
      return (
        list.find((entry) => entry.logsTemplate?.id === qaqcLogsTemplateId) ?? list.at(0) ?? null
      );
    }
    return logEntriesData?.logEntriesByReference?.[0] ?? null;
  }, [
    isQaqcPlacement,
    logEntriesData?.logEntriesByReference,
    qaqcLogEntriesData?.logEntries,
    qaqcLogsTemplateId,
  ]);

  const queryLoading = isQaqcPlacement ? qaqcQueryLoading : worklogQueryLoading;
  const refetchAttachments = isQaqcPlacement ? refetchQaqcAttachments : refetchWorklogAttachments;

  // Backend filters by logDate + shift, so we get at most one entry (per template for QA/QC)
  const currentLogEntryId = currentLogEntry?.id ?? null;
  const logEntryFiles = useMemo(() => currentLogEntry?.files ?? [], [currentLogEntry?.files]);

  const processFileToAttachment = useCallback(
    async (file: {
      id?: string | null;
      fileName?: string | null;
      name?: string | null;
      type?: string | null;
      resourcePath?: string | null;
      url?: string | null;
      caption?: string | null;
      uploadStatus?: FileUploadStatus | null;
      uuid?: string | null;
      imagePosition?: number | null;
    }) => {
      const {
        fileName,
        name,
        resourcePath,
        url: fileUrlFromQuery,
        type,
        id,
        uploadStatus,
        caption,
        uuid,
        imagePosition,
      } = file;

      if (!id) {
        return null;
      }

      const fileUrl = fileUrlFromQuery || (resourcePath ? getCloudfrontUrl(resourcePath) : '');

      if (!fileUrl) {
        return null;
      }

      const isImage =
        type &&
        (type.includes('image/') || [FileTypes.Jpeg, FileTypes.Png].includes(type as FileTypes));

      let base64: string | undefined;
      if (isImage && fileUrl) {
        try {
          base64 = await urlToBase64(fileUrl);
        } catch {
          base64 = undefined;
        }
      }

      return {
        id: id,
        name: fileName || name || 'Unknown',
        fileName: fileName || name,
        type: type || 'unknown',
        base64,
        isNew: false,
        caption: caption || undefined,
        url: fileUrl,
        resourcePath: resourcePath || undefined,
        uploadStatus: uploadStatus || FileUploadStatus.Success,
        uuid: uuid || undefined,
        imagePosition: imagePosition === null ? undefined : imagePosition,
      };
    },
    []
  );

  useEffect(() => {
    if (!cookiesFetched || queryLoading) {
      setIsProcessingAttachments(false);
      return;
    }

    const fetchImageURLs = async () => {
      setIsProcessingAttachments(true);
      const validFiles = logEntryFiles.filter(
        (file): file is typeof file & { id: string } =>
          !!file.id && file.uploadStatus === FileUploadStatus.Success
      );

      if (validFiles.length === 0) {
        setProcessedAttachments([]);
        setIsProcessingAttachments(false);
        return;
      }

      try {
        const fetchedAttachments = await Promise.all(
          validFiles.map((file) => processFileToAttachment(file))
        );
        const validFetchedAttachments = fetchedAttachments.filter(Boolean) as Attachment[];

        if (validFetchedAttachments.length > 0) {
          setProcessedAttachments(validFetchedAttachments);
        } else {
          setProcessedAttachments([]);
        }
      } finally {
        setIsProcessingAttachments(false);
      }
    };

    fetchImageURLs();
  }, [logEntryFiles, cookiesFetched, processFileToAttachment, queryLoading]);

  const isAttachmentsLoading = !cookiesFetched || queryLoading || isProcessingAttachments;

  const [uploadAttachments] = useUploadLogEntryAttachmentsMutation();
  const [updateFileStatus] = useUpdateLogEntryAttachmentFileStatusMutation();
  const [removeAttachments] = useRemoveLogEntryAttachmentsMutation();
  const [upsertLogEntry] = useUpsertLogEntryMutation();

  const ensureLogEntryId = useCallback(async () => {
    if (currentLogEntryId) {
      return currentLogEntryId;
    }

    let logEntryInput: any = {};

    if (isQaqcPlacement) {
      if (!selectedWorkpackId || !qaqcLogsTemplateId) {
        return null;
      }
      logEntryInput = {
        logDate: formattedDate,
        structureId,
        referenceId: selectedWorkpackId,
        collectionName: COLLECTION_NAMES.WORKPACK,
        category: LogCategory.QaqcForm,
        logsTemplateId: qaqcLogsTemplateId,
        shift: selectedShift,
        fieldValues: [],
        lastModifiedFor: 'QA/QC log attachments',
      };
    } else if (isCostLog) {
      if (!platformId || !selectedCostLogsTemplate?.id) {
        return null;
      }
      logEntryInput = {
        logDate: formattedDate,
        structureId,
        referenceId: platformId,
        collectionName: COLLECTION_NAMES.PLATFORM,
        category: LogCategory.CostLog,
        logsTemplateId: selectedCostLogsTemplate?.id,
        shift: ShiftType.Day,
        fieldValues: [],
        ...(selectedWbsFilter && { type: selectedWbsFilter }),
        lastModifiedFor: 'Cost log attachments',
      };
    } else {
      if (!selectedWorkpackId || !selectedWorklogsTemplate?.id) {
        return null;
      }

      logEntryInput = {
        logDate: formattedDate,
        structureId,
        referenceId: selectedWorkpackId,
        collectionName: COLLECTION_NAMES.WORKPACK,
        category: LogCategory.Worklog,
        logsTemplateId: selectedWorklogsTemplate?.id,
        shift: selectedShift ?? ShiftType.Day,
        fieldValues: [],
        lastModifiedFor: 'Work log attachments',
      };
    }

    const result = await upsertLogEntry({
      variables: {
        input: logEntryInput,
      },
      refetchQueries: [
        GetAggregatedKpiLogEntriesDocument,
        GetLogEntryCardsDocument,
        LogEntriesByReferenceDocument,
      ],
    });

    return result.data?.upsertLogEntry?.id ?? null;
  }, [
    currentLogEntryId,
    formattedDate,
    isCostLog,
    isQaqcPlacement,
    platformId,
    qaqcLogsTemplateId,
    selectedCostLogsTemplate?.id,
    selectedShift,
    selectedWbsFilter,
    selectedWorklogsTemplate?.id,
    selectedWorkpackId,
    structureId,
    upsertLogEntry,
  ]);

  const handleAddAttachment = useCallback(async () => {
    const logEntryId = await ensureLogEntryId();
    if (!logEntryId) {
      showSnackBar('Unable to prepare log entry. Please try again.', 'error');
      return;
    }
    setOpenUploadDialog(true);
  }, [ensureLogEntryId, showSnackBar]);

  const handleDownloadAttachment = useCallback(
    (attachmentIndex: number) => {
      const currentAttachment = processedAttachments[attachmentIndex];
      if (!currentAttachment.url) return;
      downloadFile(currentAttachment.url, currentAttachment.name);
    },
    [processedAttachments]
  );

  const handleRemoveAttachment = useCallback(
    (attachmentIndex: number) => {
      if (readOnly) {
        return;
      }
      const currentAttachment = processedAttachments[attachmentIndex];
      if (!currentAttachment.id) {
        showSnackBar('Unable to remove attachment: ID not available', 'error');
        return;
      }

      setAttachmentIndexToRemove(attachmentIndex);
      setIsRemoveDialogOpen(true);
    },
    [processedAttachments, readOnly, showSnackBar]
  );

  const handleConfirmRemove = useCallback(async () => {
    if (attachmentIndexToRemove === null) {
      setIsRemoveDialogOpen(false);
      return;
    }

    const currentAttachment = processedAttachments[attachmentIndexToRemove];
    if (!currentAttachment.id) {
      showSnackBar('Unable to remove attachment: ID not available', 'error');
      setIsRemoveDialogOpen(false);
      setAttachmentIndexToRemove(null);
      return;
    }

    if (!currentLogEntryId) {
      showSnackBar('Log entry not found. Save the log first.', 'error');
      setIsRemoveDialogOpen(false);
      setAttachmentIndexToRemove(null);
      return;
    }

    setIsRemoving(true);
    try {
      const result = await removeAttachments({
        variables: {
          input: {
            logEntryId: currentLogEntryId,
            structureId: structureId ?? '',
            fileIds: [currentAttachment.id],
            lastModifiedFor: 'Attachment removed successfully',
          },
        },
        refetchQueries: isQaqcPlacement ? [] : [LogEntriesByReferenceDocument],
      });

      if (result.data?.removeLogEntryAttachments) {
        setProcessedAttachments((prev) =>
          prev.filter((attachment) => attachment.id !== currentAttachment.id)
        );
        showSnackBar('Attachment removed successfully', 'success');
        await refetchAttachments();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove attachment';
      showSnackBar(errorMessage, 'error');
    } finally {
      setIsRemoving(false);
      setIsRemoveDialogOpen(false);
      setAttachmentIndexToRemove(null);
    }
  }, [
    attachmentIndexToRemove,
    currentLogEntryId,
    processedAttachments,
    structureId,
    removeAttachments,
    showSnackBar,
    refetchAttachments,
    isQaqcPlacement,
  ]);

  const handleCancelRemove = useCallback(() => {
    setIsRemoveDialogOpen(false);
    setAttachmentIndexToRemove(null);
  }, []);

  const handleUploadAttachments = async (attachmentsToUpload: KpiAttachment[]) => {
    const logEntryId = await ensureLogEntryId();
    if (!logEntryId) {
      return;
    }

    try {
      const result = await uploadAttachments({
        variables: {
          input: {
            logEntryId: logEntryId,
            structureId: structureId ?? '',
            shift: isCostLog ? ShiftType.Day : (selectedShift ?? ShiftType.Day),
            logDate: formattedDate,
            timezone: userTimezone,
            files: attachmentsToUpload
              .filter(({ file, uuid }) => file && uuid)
              .map((attachment) => ({
                uuid: attachment.uuid || '',
                name: attachment.name,
                size: attachment.size || 0,
                mimeType: attachment.type,
              })),
            lastModifiedFor: 'Attachments uploaded to log entry',
          },
        },
        refetchQueries: isQaqcPlacement ? [] : [LogEntriesByReferenceDocument],
      });

      const uploadedFiles = result.data?.uploadLogEntryAttachments ?? [];
      if (uploadedFiles.length === 0) {
        showSnackBar('Failed to get upload URLs', 'error');
        return;
      }

      for (const { url, id, uuid } of uploadedFiles) {
        const attachment = attachmentsToUpload.find((att) => att.uuid === uuid);
        if (!attachment?.file) {
          continue;
        }

        try {
          const uploadResponse = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': attachment.type },
            body: attachment.file,
          });

          if (uploadResponse.status === 200) {
            await updateFileStatus({
              variables: {
                input: {
                  logEntryId: logEntryId,
                  fileId: id ?? '',
                  uploadStatus: FileUploadStatus.Success,
                },
              },
              refetchQueries: isQaqcPlacement ? [] : [LogEntriesByReferenceDocument],
            });
          }
        } catch (error) {
          console.error('Failed to update status', error);
          showSnackBar('Failed to update status', 'error');
        }
      }

      if (isQaqcPlacement) {
        await refetchQaqcAttachments();
      }
      showSnackBar('Attachment(s) uploaded successfully', 'success');
      setAttachments([]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload attachments';
      console.error('Failed to upload attachments', error);
      showSnackBar(errorMessage, 'error');
    }
  };

  const readOnlyAttachmentLabel =
    processedAttachments.length > 0 ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flex: 1,
        }}
      >
        <AttachFileIcon sx={{ color: abyssColors.primary[300], fontSize: '2rem' }} />
        <Typography variant="body2" color={abyssColors.primary[300]}>
          {attachmentHelpText.withFiles}
        </Typography>
      </Box>
    ) : null;

  const attachmentUploadAreaContent = readOnly ? (
    readOnlyAttachmentLabel
  ) : (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flex: 1,
        }}
      >
        <AttachFileIcon sx={{ color: abyssColors.primary[300], fontSize: '2rem' }} />
        <Typography variant="body2" color={abyssColors.primary[300]}>
          {processedAttachments.length === 0
            ? attachmentHelpText.empty
            : attachmentHelpText.withFiles}
        </Typography>
      </Box>
      <Button
        id="kpi-tracker-upload-attachments-button"
        variant="outlined"
        startIcon={<AbyssIcon iconName="file-upload" size="md" />}
        onClick={handleAddAttachment}
        sx={{ ...textButtonStyle, px: '1.4rem', height: 26, fontSize: '1.2rem' }}
      >
        Upload
      </Button>
    </>
  );

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Accordion
        expanded={attachmentsExpanded}
        onChange={() => setAttachmentsExpanded(!attachmentsExpanded)}
        disableGutters
        sx={{ ...accordionStyles, pb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={getAccordionSummaryStyles(attachmentsExpanded)}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 500, fontSize: '1.4rem', color: abyssColors.primary[500] }}
          >
            Attachments
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={accordianDetailsStyles}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isAttachmentsLoading && (
              <Stack
                direction="row"
                spacing={2}
                sx={{ overflowX: 'auto', pb: 1 }}
                id="kpi-attachments"
              >
                <Stack
                  alignItems="center"
                  gap="0.4rem"
                  sx={{
                    minWidth: '10rem',
                    flexShrink: 0,
                    pt: '0.6rem',
                    pr: '0.6rem',
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      borderRadius: '0.4rem',
                      border: `0.1rem solid ${abyssColors.primary[200]}`,
                    }}
                    width="10rem"
                    height="10rem"
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    sx={{ backgroundColor: 'transparent' }}
                    width="10rem"
                    height={20}
                  />
                </Stack>
                <Stack
                  alignItems="center"
                  gap="0.4rem"
                  sx={{
                    minWidth: '10rem',
                    flexShrink: 0,
                    pt: '0.6rem',
                    pr: '0.6rem',
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      borderRadius: '0.4rem',
                      border: `0.1rem solid ${abyssColors.primary[200]}`,
                    }}
                    width="10rem"
                    height="10rem"
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    sx={{ backgroundColor: 'transparent' }}
                    width="10rem"
                    height={20}
                  />
                </Stack>
                <Stack
                  alignItems="center"
                  gap="0.4rem"
                  sx={{
                    minWidth: '10rem',
                    flexShrink: 0,
                    pt: '0.6rem',
                    pr: '0.6rem',
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      borderRadius: '0.4rem',
                      border: `0.1rem solid ${abyssColors.primary[200]}`,
                    }}
                    width="10rem"
                    height="10rem"
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    sx={{ backgroundColor: 'transparent' }}
                    width="10rem"
                    height={20}
                  />
                </Stack>
              </Stack>
            )}
            {!isAttachmentsLoading && readOnly && processedAttachments.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  py: 1,
                }}
              >
                <AttachFileIcon sx={{ color: abyssColors.primary[300], fontSize: '2rem' }} />
                <Typography variant="body2" color={abyssColors.primary[300]}>
                  {attachmentHelpText.empty}
                </Typography>
              </Box>
            ) : null}
            {!isAttachmentsLoading && (!readOnly || processedAttachments.length > 0) ? (
              <Box
                sx={{
                  border: readOnly ? 'none' : '0.2rem dashed',
                  borderColor: abyssColors.primary[200],
                  borderRadius: '0.8rem',
                  p: readOnly ? 0 : 1.2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  backgroundColor: readOnly ? 'transparent' : abyssColors.primary[50],
                }}
              >
                {attachmentUploadAreaContent}
              </Box>
            ) : null}
            {!isAttachmentsLoading && processedAttachments.length > 0 && (
              <Grid container spacing={2} columns={3} id="kpi-attachments">
                <MediaViewer
                  collectionName={KPI_LOG_ENTRY_ATTACHMENTS_COLLECTION_NAME}
                  mediaFiles={processedAttachments.filter(
                    (file) =>
                      file &&
                      (file.type?.includes('image/') ||
                        [FileTypes.Jpeg, FileTypes.Png].includes(file.type as FileTypes))
                  )}
                  handleDownloadAttachment={readOnly ? undefined : handleDownloadAttachment}
                  handleRemoveAttachment={readOnly ? undefined : handleRemoveAttachment}
                >
                  {processedAttachments
                    ?.filter((file) => !!file)
                    .map((attachment, index) => (
                      <Grid key={attachment.id || attachment.name || `attachment-${index}`}>
                        <FileDisplay
                          collectionName={KPI_LOG_ENTRY_ATTACHMENTS_COLLECTION_NAME}
                          selectedAttachment={attachment}
                          selectedAttachmentIndex={index}
                          showRemoveIcon={false}
                          enableUpload={false}
                          handleRemoveAttachment={readOnly ? undefined : handleRemoveAttachment}
                          handleDownloadAttachment={readOnly ? undefined : handleDownloadAttachment}
                        />
                      </Grid>
                    ))}
                </MediaViewer>
              </Grid>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      <UploadAttachmentsDialog
        openDialog={openUploadDialog}
        setOpenDialog={setOpenUploadDialog}
        attachments={attachments}
        setAttachments={setAttachments}
        onUpload={handleUploadAttachments}
      />

      <GenericDialog
        open={isRemoveDialogOpen}
        onClose={handleCancelRemove}
        title="Remove Attachment"
        content={
          <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
            Are you sure you want to remove this attachment?
          </Typography>
        }
        actions={
          <>
            <Button
              variant="text"
              onClick={handleCancelRemove}
              disabled={isRemoving}
              sx={{ color: abyssColors.primary[500] }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmRemove}
              disabled={isRemoving}
              sx={containedButtonStyle}
              startIcon={isRemoving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isRemoving ? 'Removing...' : 'Remove'}
            </Button>
          </>
        }
        actionsStyles={{ mr: 3, pb: 3 }}
      />
    </Box>
  );
};
